import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
  EmptystateListColored,
  EmptystateSearchNoResultsColored,
} from "components/icons/generated";
import { getSearchQuery } from "components/SearchInput/helpers";
import EmptyState from "ds/components/EmptyState";
import TableSimple from "ds/components/TableSimple";
import useErrorHandle from "hooks/useErrorHandle";

import PluginsLayout from "../../components/PluginsLayout";
import { sortRows } from "../../helpers/sortRows";
import { pluginsTableCells } from "./cells";
import { PLUGINS_COLUMN_ID } from "./constants";
import { GET_PLUGINS } from "./gql";
import { usePluginsTableStore } from "./usePluginsTableStore";

const Plugins = () => {
  const [searchParams] = useSearchParams();
  const { data, loading, error } = useQuery(GET_PLUGINS);

  const ErrorContent = useErrorHandle(error);

  // TODO: Temporary solution until BE implements sorting and filtering for plugins
  const filteredPlugins = useMemo(() => {
    const searchQuery = getSearchQuery(searchParams);

    const filtered =
      data?.plugins.filter((plugin) =>
        plugin.name.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase())
      ) ?? [];

    const sorted = sortRows(searchParams, filtered, PLUGINS_COLUMN_ID.NAME);

    return sorted;
  }, [searchParams, data?.plugins]);

  const noInstalledPlugins = !data?.plugins.length && !filteredPlugins.length && !loading;
  const noSearchResults = !!data?.plugins.length && filteredPlugins.length === 0;
  const hasPluginItems = filteredPlugins.length > 0;

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <PluginsLayout>
      {noSearchResults && (
        <EmptyState
          title="No results"
          caption="Don't give up! Check the spelling or try something less specific."
          icon={EmptystateSearchNoResultsColored}
        />
      )}
      {noInstalledPlugins && (
        <EmptyState
          title="No plugins installed yet"
          caption="You can install plugins to extend the functionality of your Spacelift account."
          icon={EmptystateListColored}
        />
      )}
      {hasPluginItems && (
        <TableSimple
          data={filteredPlugins}
          loading={loading}
          store={usePluginsTableStore}
          ariaLabel="Plugins table"
          filtersStorageKey="plugins-filters"
          cellPerColumn={pluginsTableCells}
        />
      )}
    </PluginsLayout>
  );
};

export default Plugins;
